import type { GridClipboardPayload, GridClipboardReadResult } from './types';

/**
 * Custom flavor for same-browser transfers. All three engines round-trip it
 * through the sync copy/paste events; WebKit additionally scopes it to the
 * origin that wrote it. It does NOT survive a cross-browser hop — that path
 * relies on the payload embedded in the text/html flavor instead.
 */
export const GRID_CLIPBOARD_MIME = 'application/x-niit-grid+json';

export const GRID_CLIPBOARD_SCHEMA_VERSION = 1;

/**
 * The full payload rides base64-encoded in a single attribute on the root
 * <table> — never sharded across cells. The <td> contents are plain scalar
 * renderings for Excel/Sheets; our own paste path ignores them whenever the
 * attribute is present.
 */
const PAYLOAD_ATTR = 'data-niit-grid';

export function createGridClipboardPayload<TData>(
  args: Omit<GridClipboardPayload<TData>, 'copyId' | 'schemaVersion'>,
): GridClipboardPayload<TData> {
  return { ...args, copyId: crypto.randomUUID(), schemaVersion: GRID_CLIPBOARD_SCHEMA_VERSION };
}

/**
 * Populate all clipboard flavors from inside a `copy` event. Must run on the
 * sync path (`event.clipboardData`): the async Clipboard API rejects custom
 * MIME types outside Chromium and sanitizes HTML, which would strip the
 * payload attribute.
 *
 * `scalars` is the rectangle's scalar projection — same dimensions as the
 * payload's rowKeys × columnKeys, one string per cell (empty string for cells
 * with no sensible scalar form).
 */
export function writeGridClipboard<TData>(
  clipboardData: DataTransfer,
  payload: GridClipboardPayload<TData>,
  scalars: readonly (readonly string[])[],
): void {
  const json = JSON.stringify(payload);
  clipboardData.setData(GRID_CLIPBOARD_MIME, json);
  clipboardData.setData('text/html', buildHtml(json, scalars));
  clipboardData.setData('text/plain', buildTsv(scalars));
}

/**
 * Probe the flavors of a `paste` event, richest first: custom MIME → payload
 * attribute in the HTML flavor → foreign HTML table → plain-text TSV.
 * Callers must still reconcile `payload.copyId` / `gridId` / `schemaVersion`
 * against what they can apply — a `grid` result only means the bytes are ours.
 */
export function readGridClipboard<TData = unknown>(clipboardData: DataTransfer): GridClipboardReadResult<TData> {
  const plainScalars = parseTsv(clipboardData.getData('text/plain'));

  const native = clipboardData.getData(GRID_CLIPBOARD_MIME);
  const nativePayload = native ? parsePayload<TData>(native) : null;
  if (nativePayload !== null) return { kind: 'grid', payload: nativePayload, scalars: plainScalars };

  const html = clipboardData.getData('text/html');
  if (html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const encoded = doc.querySelector(`table[${PAYLOAD_ATTR}]`)?.getAttribute(PAYLOAD_ATTR);
    const embeddedPayload = encoded ? parsePayload<TData>(tryDecodeBase64(encoded)) : null;
    if (embeddedPayload !== null) return { kind: 'grid', payload: embeddedPayload, scalars: plainScalars };

    const table = doc.querySelector('table');
    if (table !== null) return { kind: 'external', scalars: tableScalars(table) };
  }

  if (plainScalars.length > 0) return { kind: 'external', scalars: plainScalars };
  return { kind: 'empty' };
}

function parsePayload<TData>(json: string): GridClipboardPayload<TData> | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  return isGridPayload(parsed) ? (parsed as GridClipboardPayload<TData>) : null;
}

function isGridPayload(value: unknown): value is GridClipboardPayload {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.copyId === 'string' &&
    typeof candidate.gridId === 'string' &&
    candidate.schemaVersion === GRID_CLIPBOARD_SCHEMA_VERSION &&
    Array.isArray(candidate.columnKeys) &&
    Array.isArray(candidate.rowKeys)
  );
}

function buildHtml(payloadJson: string, scalars: readonly (readonly string[])[]): string {
  const body = scalars
    .map((row) => `<tr>${row.map((value) => `<td>${escapeHtml(value)}</td>`).join('')}</tr>`)
    .join('');
  return `<table ${PAYLOAD_ATTR}="${encodeBase64(payloadJson)}"><tbody>${body}</tbody></table>`;
}

function tableScalars(table: HTMLTableElement): string[][] {
  return Array.from(table.rows, (row) => Array.from(row.cells, (cell) => cell.textContent?.trim() ?? ''));
}

function escapeHtml(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');
}

function buildTsv(scalars: readonly (readonly string[])[]): string {
  return scalars.map((row) => row.map(escapeTsvValue).join('\t')).join('\n');
}

/** Excel-compatible quoting: only values containing tabs, newlines or quotes get wrapped. */
function escapeTsvValue(value: string): string {
  return /[\t\n\r"]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

function parseTsv(text: string): string[][] {
  if (text === '') return [];
  const rows: string[][] = [];
  let row: string[] = [];
  let value = '';
  let inQuotes = false;
  let i = 0;
  while (i < text.length) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          value += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      value += char;
      i += 1;
      continue;
    }
    if (char === '"' && value === '') {
      inQuotes = true;
    } else if (char === '\t') {
      row.push(value);
      value = '';
    } else if (char === '\n' || char === '\r') {
      row.push(value);
      rows.push(row);
      row = [];
      value = '';
      if (char === '\r' && text[i + 1] === '\n') i += 1;
    } else {
      value += char;
    }
    i += 1;
  }
  if (value !== '' || row.length > 0) {
    row.push(value);
    rows.push(row);
  }
  return rows;
}

/**
 * Unicode-safe base64: encodeURIComponent-style byte round trip, chunked so
 * large rectangles don't blow the argument-spread limit.
 */
function encodeBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

function tryDecodeBase64(encoded: string): string {
  try {
    const binary = atob(encoded);
    return new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)));
  } catch {
    return '';
  }
}
